import React from 'react'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@mui/material/CircularProgress'
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator'
import { useStyle } from '../../../../AccessBank/PoliciesProcessing/Components/PolicyHeader/Style'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import AlertDialog from '../../../../../components/AlertDialog/index.es6'
import ModalInc from '../../../../../shared-ui/ModalInc'
import {
  hasAuthourity,
  getPartnerGuid,
  isFeatureFlagEnabled,
  hasOmmAmlManagementRole
} from '../../../../../lib/access.es6'
import { PARTNERS_GUID_ENUM } from '../../../../../routes/PartnersEnum'
import MakerChecks from '../MakerChecks/MakerChecks'
import { stageCanRequestChanges } from '../../../../../containers/PolicyApprovalDashboard/stages'
import { hasUndecidedFlag } from '../../../../AccessBank/PoliciesProcessing/lib/flaggedPolicyDecision'
import { reloadPage } from '../../../../../lib/utils.es6'
import {
  isAmlStageCurrent,
  isEscalatedToManagement
} from '../../../lib/makerCheckerQueues'

// Fork of AccessBank/PoliciesProcessing/Components/PolicyHeader. Rendered only
// for OMM, so the OMK and CoverLink branches it inherits are inert.

const STAGE_DESCRIPTIONS = {
  AmlApproval:
    "Verify the customer's Anti-Money Laundering and PEP screening results before approving.",
  AgentApproval:
    "Review the policy details for completeness and accuracy before proceeding. Use 'Request Changes' if the policy needs updates — this sends it to the capturing agent's action basket for correction."
}

function getStageInfo(policy, status) {
  if (!policy) {
    return {
      label: 'Policy Processing',
      description: '',
      bg: '#F5F5F5',
      borderColor: '#9E9E9E'
    }
  }
  if (Array.isArray(policy.maker_checks) && policy.maker_checks.length > 0) {
    const sorted = policy.maker_checks.slice().sort(function(a, b) {
      return (a.sequence || 0) - (b.sequence || 0)
    })
    let activeCk = null
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].status === 'Pending') {
        activeCk = sorted[i]
        break
      }
    }
    if (activeCk) {
      const needAction =
        policy.additional_details && policy.additional_details.need_action
      const amlNeedAction =
        policy.additional_details && policy.additional_details.aml_need_action
      if (needAction) {
        return {
          label: 'Changes Requested — Action Required',
          description:
            "The reviewer has requested changes to this policy. Review the feedback, make the necessary updates, then click 'Send back for approval'.",
          bg: '#FFF3E0',
          borderColor: '#E65100'
        }
      }
      if (amlNeedAction) {
        return {
          label: 'AML Action Required',
          description:
            "The AML reviewer has flagged this policy for action. Review the feedback in the policy notes, resolve the issue, then click 'Send Back for Approval'.",
          bg: '#FFF3E0',
          borderColor: '#E65100'
        }
      }
      return {
        label: activeCk.label || activeCk.type,
        description:
          STAGE_DESCRIPTIONS[activeCk.type] ||
          'Review this stage and take action to proceed.',
        bg: '#EEF3FA',
        borderColor: '#1565C0'
      }
    }
  }
  if (status === 'Rejected') {
    return {
      label: 'Policy Rejected',
      description:
        'This policy has been rejected. The responsible party may re-confirm or  discard it permanently.',
      bg: '#FDF0F0',
      borderColor: '#C62828'
    }
  }
  if (status === 'Unpaid') {
    return {
      label: 'Awaiting First Premium',
      description:
        'All approval stages are complete. The policy is now active and awaiting collection of the first premium payment.',
      bg: '#EDF7ED',
      borderColor: '#388E3C'
    }
  }
  if (status === 'Discarded') {
    return {
      label: 'Policy Discarded',
      description: 'This policy has been permanently discarded.',
      bg: '#F5F5F5',
      borderColor: '#9E9E9E'
    }
  }
  return {
    label: 'Policy Processing',
    description: '',
    bg: '#F5F5F5',
    borderColor: '#9E9E9E'
  }
}

// An AML/PEP flagged policy must be decided from the AML/PEP panel on the
// policy details (PolicyDetailsFlp "Take Action"), which requires a reason and
// a supporting document. The plain header Approve/Reject would activate or
// reject the policy without either, and without clearing the flag, so it is
// withheld for these partners while a flag is still undecided.
const FLAGGED_DECISION_GATED_PARTNERS = [PARTNERS_GUID_ENUM.OLDMUTUAL_KENYA]

// Approve/Reject is only meaningful once an operator is actively working the
// review queue. Viewing a policy from anywhere else (Recent Policies,
// customer status, search, amended-policies) should not offer the decision —
// see cameFromPolicyDashboard() in PoliciesProcessing/index.js.
const DASHBOARD_ONLY_DECISION_PARTNERS = [PARTNERS_GUID_ENUM.OLDMUTUAL_KENYA]

const PolicyHeader = props => {
  const classes = useStyle()
  const [showWarningReject, setShowWarningReject] = React.useState(false)
  const [sendToReviewEnabled, setSendToReviewEnabled] = React.useState(false)
  const [requestChangesEnabled, setRequestChangesEnabled] = React.useState(
    false
  )
  const [showRequestChangesModal, setShowRequestChangesModal] = React.useState(
    false
  )
  const [requestChangesNotes, setRequestChangesNotes] = React.useState('')

  React.useEffect(function() {
    isFeatureFlagEnabled('omm_send_to_review_enabled').then(
      setSendToReviewEnabled
    )
    isFeatureFlagEnabled('omm_request_changes_enabled').then(
      setRequestChangesEnabled
    )
  }, [])

  const POLICY_STATUSES = {
    Un_Confirmed: 'Un_Confirmed',
    Rejected: 'Rejected',
    Discarded: 'Discarded'
  }

  const status = props.policy ? props.policy.status : null

  const isOMM = getPartnerGuid() === PARTNERS_GUID_ENUM.OLDMUTUAL_MALAWI
  const policyNeedAction =
    props.policy &&
    props.policy.additional_details &&
    props.policy.additional_details.need_action

  const useMakerChecks =
    props.policy &&
    Array.isArray(props.policy.maker_checks) &&
    props.policy.maker_checks.length > 0

  // CoverLink's later approval level can send a policy back to the level below
  // for changes instead of rejecting it outright — see
  // containers/PolicyApprovalDashboard/stages.js. Only offered on the stages
  // configured for it, so the first level (which has nowhere below to send it)
  // keeps the plain Approve/Reject pair.
  const flaggedDecisionGated =
    FLAGGED_DECISION_GATED_PARTNERS.indexOf(getPartnerGuid()) !== -1 &&
    hasUndecidedFlag(props.policy)

  const isCoverLink = getPartnerGuid() === PARTNERS_GUID_ENUM.COVER_LINK

  const amlStageCurrent = isAmlStageCurrent(props.policy)
  const escalatedToManagement = isEscalatedToManagement(props.policy)
  // TODO(omm-desk-authorities): should be the Management authority, so the AML
  // Desk cannot decide its own escalation.
  const canDecideEscalation = hasAuthourity('APPROVE_POLICY')

  const allowRequestChanges =
    (isCoverLink && stageCanRequestChanges(props.policy)) ||
    (requestChangesEnabled && !amlStageCurrent)

  const requireDashboardOrigin =
    DASHBOARD_ONLY_DECISION_PARTNERS.indexOf(getPartnerGuid()) !== -1
  const dashboardGatePasses =
    !requireDashboardOrigin || !!props.cameFromPolicyDashboard

  // additional_details.need_action carries TWO different meanings, and neither
  // is declared in policy-administration-complex — it is a frontend-owned flag
  // that the backend stores without interpreting:
  //
  //   OMM       — set by ReConfirm, meaning "the capturing agent must redo
  //               this", and cleared on approval.
  //   CoverLink — set by the insurer's Request Changes, meaning "sent back to
  //               the brokers, and theirs to act on".
  //
  // Both partners write and clear the same key. That is safe only because every
  // read of it is partner-gated, as here: a policy is never shown through the
  // other partner's interpretation. Anything new reading this flag must gate on
  // the partner too, or give the value its own discriminator
  // (e.g. need_action: 'coverlink_sent_back') and migrate both writers.
  // Without this a returned policy shows the desk its decision buttons and the
  // capturing agent has no way to resubmit.
  const sentBackForChanges = !!policyNeedAction

  // An AML "changes required" flag is also the capturing agent's to resolve.
  const flaggedForAmlAction = !!(
    isOMM &&
    props.policy &&
    props.policy.additional_details &&
    props.policy.additional_details.aml_need_action
  )

  // A policy sent back for changes belongs to the level below, and on some
  // partners that level only captures policies rather than approving them.
  // Without this they could not see the control at all, and the policy would
  // have nobody able to return it. The control offers them only "Send Back for
  // Approval" — deciding the stage still needs APPROVE_POLICY.
  const isApprover = hasAuthourity('APPROVE_POLICY')

  // A capture-only OMM agent (no APPROVE_POLICY) must still reach the button
  // row when there is a real Send to Review action for them — but ONLY then.
  // They must never see Approve/Reject/Request Changes, which stay gated on
  // isApprover inside the row below.
  const agentCanSendToReview =
    isOMM &&
    status === POLICY_STATUSES.Un_Confirmed &&
    policyNeedAction &&
    hasAuthourity('CAPTURE_POLICY') &&
    sendToReviewEnabled

  const canActOnUnconfirmed = useMakerChecks
    ? isApprover ||
      ((sentBackForChanges || flaggedForAmlAction) &&
        hasAuthourity('CAPTURE_POLICY')) ||
      (escalatedToManagement && canDecideEscalation)
    : isApprover || agentCanSendToReview

  const rejectPolicy = () => {
    setShowWarningReject(false)
    props.toggleOpenRejectPolicyDialog()
  }

  // For a Rejected policy the two actions are gated separately:
  // - Re-Confirm (agents re-submit the policy) requires CAPTURE_POLICY
  // - Discard (approvers reject it permanently) requires APPROVE_POLICY
  const canReConfirmRejected =
    status === POLICY_STATUSES.Rejected && hasAuthourity('CAPTURE_POLICY')
  const canDiscardRejected =
    status === POLICY_STATUSES.Rejected && hasAuthourity('APPROVE_POLICY')

  // Send to Review / Request Changes both go through the same setNeedAction
  // handoff as MakerChecks' own copy of this handoff (see MakerChecks.js), but
  // OMM's plain Approve/Reject branch below never rendered any of it, so
  // clicking either button fired the request with no visible feedback and no
  // refresh of the policy afterward — indistinguishable from doing nothing.
  const readError = error => {
    if (!error) return ''
    if (typeof error === 'string') return error
    if (Array.isArray(error.message)) return error.message.join(', ')
    if (error.message) return error.message
    return 'An unexpected error occurred'
  }

  const sendingBack = props.setNeedActionDirection === 'requestChanges'

  const needActionProgress = sendingBack
    ? `Sending policy ${props.policyNumber} back for changes...`
    : `Sending policy ${props.policyNumber} to review...`

  const needActionSuccessMessage = sendingBack
    ? `Policy ${props.policyNumber} was sent back for changes.`
    : `Policy ${props.policyNumber} was sent to review.`

  const needActionErrorTitle = sendingBack
    ? `Could not send policy ${props.policyNumber} back for changes`
    : `Could not send policy ${props.policyNumber} to review`

  const stageInfo = getStageInfo(props.policy, status)

  return (
    <section
      style={{
        background: stageInfo.bg,
        borderLeft: `5px solid ${stageInfo.borderColor}`,
        borderRadius: '8px'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          gap: '24px'
        }}
      >
        {/* Left: current stage name + description */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: 'Lato',
              fontWeight: 700,
              fontSize: '15px',
              color: stageInfo.borderColor,
              marginBottom: '6px'
            }}
          >
            {stageInfo.label}
          </div>
          {flaggedDecisionGated ? (
            <>
              <div
                style={{
                  fontFamily: 'Open Sans',
                  fontSize: '13px',
                  color: '#424242',
                  lineHeight: 1.5
                }}
              >
                Policy flagged for:{' '}
                {props.policy.additional_details &&
                props.policy.additional_details.pep_flagged
                  ? `PEP reason: ${props.policy.additional_details.pep_position}`
                  : props.policy.additional_details &&
                    props.policy.additional_details.aml_flagged
                  ? `AML reason: ${props.policy.additional_details.aml_reason}`
                  : 'No Reason Provided'}
              </div>
              <div
                style={{
                  fontFamily: 'Open Sans',
                  fontSize: '13px',
                  color: '#616161',
                  lineHeight: 1.5,
                  marginTop: '4px'
                }}
              >
                Approve or reject this policy from the AML/PEP section of the
                policy details below. A reason and a supporting document are
                required.
              </div>
            </>
          ) : (
            <div
              style={{
                fontFamily: 'Open Sans',
                fontSize: '13px',
                color: '#424242',
                lineHeight: 1.5
              }}
            >
              {stageInfo.description}
            </div>
          )}
        </div>

        {/* Right: action buttons */}
        <div style={{ flexShrink: 0 }}>
          {status === POLICY_STATUSES.Un_Confirmed &&
            canActOnUnconfirmed &&
            (useMakerChecks ? (
              <MakerChecks
                makerChecks={props.policy.maker_checks}
                policy={props.policy}
                policyGuid={props.policy.guid}
                uploadDocument={props.DocumentUpload}
                policyNumber={props.policyNumber}
                decideMakerCheck={props.decideMakerCheck}
                requireDashboardOrigin={requireDashboardOrigin}
                dashboardGatePasses={dashboardGatePasses}
                needAction={sentBackForChanges}
                allowRequestChanges={allowRequestChanges}
                requestChanges={props.requestChanges}
                allowEscalate={!isCoverLink && hasOmmAmlManagementRole()}
                escalate={props.escalateToManagement}
                allowAmlRequestChanges={amlStageCurrent}
                amlRequestChanges={props.amlRequestChanges}
                amlSendBackForApproval={props.amlSendBackForApproval}
                escalatedToManagement={escalatedToManagement}
                canDecideEscalation={canDecideEscalation}
                sendBackForApproval={props.sendToReview}
                loader={props.makerCheckDecisionLoader}
                success={props.makerCheckDecisionSuccess}
                error={props.makerCheckDecisionError}
                decisionResult={props.makerCheckDecision}
                needActionLoader={props.setNeedActionLoader}
                needActionSuccess={props.setNeedActionSuccess}
                needActionError={props.setNeedActionError}
                needActionDirection={props.setNeedActionDirection}
                allowReturnToPriorStage={props.allowReturnToPriorStage}
                returnToPriorStage={props.returnToPriorStage}
                returnToPriorStageLoader={props.returnToPriorStageLoader}
                returnToPriorStageSuccess={props.returnToPriorStageSuccess}
                returnToPriorStageError={props.returnToPriorStageError}
              />
            ) : (
              !flaggedDecisionGated &&
              dashboardGatePasses && (
                <div className={classes.headerButtons}>
                  {isApprover && (
                    <ButtonInc
                      onClick={() =>
                        props.setUpdatePolicyAction(props.updateActions.Approve)
                      }
                    >
                      Approve
                    </ButtonInc>
                  )}
                  {agentCanSendToReview && (
                    <ButtonInc
                      style={{ background: '#008000' }}
                      onClick={() => props.sendToReview()}
                    >
                      Send to Review
                    </ButtonInc>
                  )}
                  {isOMM &&
                    isApprover &&
                    !policyNeedAction &&
                    !amlStageCurrent &&
                    status === POLICY_STATUSES.Un_Confirmed &&
                    requestChangesEnabled && (
                      <ButtonInc
                        style={{ background: '#1976d2' }}
                        onClick={() => setShowRequestChangesModal(true)}
                      >
                        Request Changes
                      </ButtonInc>
                    )}
                  {isApprover && (
                    <ButtonInc
                      style={{ background: '#8F191C' }}
                      onClick={() => setShowWarningReject(true)}
                    >
                      Reject
                    </ButtonInc>
                  )}
                </div>
              )
            ))}

          {status === POLICY_STATUSES.Rejected &&
            (canReConfirmRejected || canDiscardRejected) && (
              <div className={classes.headerButtons}>
                {canReConfirmRejected && (
                  <ButtonInc
                    onClick={() =>
                      props.setUpdatePolicyAction(props.updateActions.ReConfirm)
                    }
                  >
                    Re-Confirm
                  </ButtonInc>
                )}
                {canDiscardRejected && (
                  <ButtonInc
                    style={{ background: '#8F191C' }}
                    onClick={() =>
                      props.setUpdatePolicyAction(props.updateActions.Discard)
                    }
                  >
                    Discard
                  </ButtonInc>
                )}
              </div>
            )}
        </div>
      </div>

      <ModalInc
        modalTitle="Request Changes"
        subTitle="Please provide notes describing the changes required."
        open={showRequestChangesModal}
        onClose={() => setShowRequestChangesModal(false)}
        fullWidth
      >
        <ValidatorForm
          onSubmit={() => {
            props.requestChanges(requestChangesNotes)
            setShowRequestChangesModal(false)
            setRequestChangesNotes('')
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12}>
              <TextValidator
                name="requestChangesNotes"
                id="requestChangesNotes"
                label="Enter notes for the requested changes"
                value={requestChangesNotes}
                onChange={event => setRequestChangesNotes(event.target.value)}
                multiline
                rows={5}
                maxRows={4}
                fullWidth
                variant="outlined"
                validators={['required']}
                errorMessages={['Please enter notes for the requested changes']}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}></Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8} md={8}>
                  <ButtonInc
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    style={{ marginTop: '20px', float: 'right' }}
                  >
                    Submit
                  </ButtonInc>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <ButtonInc
                    variant="contained"
                    color="primary"
                    size="large"
                    style={{ marginTop: '20px' }}
                    onClick={() => setShowRequestChangesModal(false)}
                  >
                    Go back
                  </ButtonInc>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ValidatorForm>
      </ModalInc>

      <AlertDialog
        warning
        show={showWarningReject}
        size="sm"
        title="Are you sure you want to reject"
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => {
          setShowWarningReject(false)
        }}
        onCancel={() => {
          rejectPolicy()
        }}
        confirmBtnText="Cancel"
        showCancel
        showConfirm
        cancelBtnText="reject"
        confirmBtnCssClass
      ></AlertDialog>

      {!useMakerChecks && (
        <>
          <AlertDialog
            custom
            show={!!props.setNeedActionLoader}
            size="sm"
            style={{ marginTop: '0', top: '30vh' }}
            showCancel={false}
            showConfirm={false}
            showTitle={false}
            confirmBtnCssClass
          >
            <CircularProgress />
            <h2>{needActionProgress}</h2>
          </AlertDialog>

          <AlertDialog
            success
            show={!!props.setNeedActionSuccess}
            size="sm"
            title={needActionSuccessMessage}
            style={{ marginTop: '0', top: '30vh' }}
            onConfirm={() => reloadPage()}
            confirmBtnText="Ok"
            showCancel={false}
            showConfirm={false}
            cancelBtnText="Cancel"
            confirmBtnCssClass
          >
            <div>
              <ButtonInc
                variant="contained"
                onClick={() => reloadPage()}
                style={{ background: 'green' }}
              >
                Ok
              </ButtonInc>
            </div>
          </AlertDialog>

          <AlertDialog
            show={!!props.setNeedActionError}
            danger
            title={needActionErrorTitle}
            onConfirm={() => reloadPage()}
            confirmBtnText="Ok"
            confirmBtnCssClass
            showCancel={false}
            style={{ marginTop: '0', top: '30vh' }}
          >
            {readError(props.setNeedActionError)}
          </AlertDialog>
        </>
      )}
    </section>
  )
}

export default PolicyHeader
